import React, { useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { 
  XMarkIcon, 
  SparklesIcon, 
  PhotoIcon,
  CheckCircleIcon,
  ArrowRightIcon 
} from '@heroicons/react/24/outline';
import { resolveMediaUrl } from '../utils/mediaUrl';
import { useUpgrade } from '../context/UpgradeContext';

const numberOrNull = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

const clamp = (n, min, max) => Math.min(Math.max(n, min), max);

const VisionCreationModal = ({ onClose, onVisionCreated, suggestionGoal }) => {
  const { openUpgrade } = useUpgrade();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    title: suggestionGoal || '',
    description: '',
    target_amount: '',
    current_amount: '0',
    image_prompt: '',
    generate_image: true
  });
  const [generating, setGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [imagePending, setImagePending] = useState(false);
  const [createdVisionId, setCreatedVisionId] = useState(null);
  const [error, setError] = useState('');

  const sanitized = useMemo(() => {
    const target = numberOrNull(formData.target_amount);
    const currentRaw = numberOrNull(formData.current_amount);
    const current = currentRaw === null ? 0 : currentRaw;
    return {
      title: (formData.title || '').trim(),
      description: (formData.description || '').trim(),
      target_amount: target,
      current_amount: current,
      generate_image: !!formData.generate_image,
      image_prompt: (formData.image_prompt || '').trim()
    };
  }, [formData]);

  const canSubmitStep1 = useMemo(() => {
    return (
      sanitized.title.length > 0 &&
      sanitized.target_amount !== null &&
      sanitized.target_amount > 0 &&
      sanitized.current_amount >= 0
    );
  }, [sanitized]);

  const setField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setField(name, type === 'checkbox' ? checked : value);
  };

  const handleDefineGoal = () => {
    if (!canSubmitStep1) {
      setError('Please enter a title, a positive target amount, and a non-negative current amount.');
      return;
    }
    setStep(2);
  };

  const buildPayload = (useImage) => {
    const target = sanitized.target_amount ?? 0;
    const currentClamped = clamp(sanitized.current_amount ?? 0, 0, Math.max(target, 0));
    return {
      title: sanitized.title,
      description: sanitized.description,
      target_amount: target,
      current_amount: currentClamped,
      image_prompt:
        useImage
          ? sanitized.image_prompt ||
            `A beautiful, inspiring visualization of ${sanitized.title}, dreamy, aspirational, photorealistic`
          : undefined,
      generate_image: !!useImage
    };
  };

  const createVision = useCallback(async (useImage) => {
    // Final guard against bad input:
    if (!canSubmitStep1) {
      setError('Please enter a title, a positive target amount, and a non-negative current amount.');
      return;
    }

    setGenerating(true);
    setError('');
    setImagePending(false);
    setCreatedVisionId(null);

    try {
      const payload = buildPayload(useImage);
      const response = await axios.post('/api/visions/create', payload, {
        timeout: 30000,
      });

      const visionId = response.data?.vision_id;
      setCreatedVisionId(visionId);
      const pending = response.data?.image_status === 'pending';
      setImagePending(pending);
      setGeneratedImage(resolveMediaUrl(response.data?.image_url) || null);
      setStep(3);

      if (pending && visionId) {
        const poll = async () => {
          for (let i = 0; i < 30; i += 1) {
            await new Promise((r) => setTimeout(r, 3000));
            try {
              const v = await axios.get(`/api/visions/${visionId}`, { timeout: 10000 });
              const status = v.data?.image_status;
              if (v.data?.image_url) {
                setGeneratedImage(resolveMediaUrl(v.data.image_url));
                setImagePending(false);
                if (typeof onVisionCreated === 'function') onVisionCreated();
                return;
              }
              if (status === 'failed' || status === 'skipped') {
                setImagePending(false);
                return;
              }
            } catch {
              /* keep polling */
            }
          }
          setImagePending(false);
        };
        poll();
      }

      // Refresh parent list when no async image (poll path calls onVisionCreated when ready)
      if (!pending && typeof onVisionCreated === 'function') onVisionCreated();
    } catch (err) {
      // Axios error introspection
      const status = err?.response?.status;
      let message = 'Failed to create vision. Please try again.';
      if (err?.code === 'ECONNABORTED' || /timeout/i.test(String(err?.message))) {
        message = useImage
          ? 'Request timed out. Image generation can take a bit—please try again.'
          : 'Request timed out. Please try again.';
      } else if (status === 400) {
        message = err?.response?.data?.detail || 'Invalid vision data. Please check your inputs.';
      } else if (status === 401) {
        message = 'You are not authenticated. Please log in and try again.';
      } else if (status === 403) {
        const detail = err?.response?.data?.detail;
        const code = typeof detail === 'object' ? detail?.error : null;
        if (code === 'vision_limit_reached') {
          onClose?.();
          openUpgrade('vision_limit');
          return;
        }
        message = typeof detail === 'object' ? detail?.message : detail || message;
      } else if (status === 500) {
        message = 'Server error occurred. Please try again later.';
      }
      setError(message);
    } finally {
      setGenerating(false);
    }
  }, [buildPayload, canSubmitStep1, onVisionCreated, onClose, openUpgrade]);

  const handleCreateWithoutImage = () => createVision(false);
  const handleCreateVision = () => createVision(!!sanitized.generate_image);

  const overlayClick = (e) => {
    // Prevent closing the modal while a creation is in-flight
    if (generating) return;
    onClose?.();
  };

  return (
    <AnimatePresence>
      <motion.div
        className="modal-overlay z-[1000]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={overlayClick}
        aria-modal="true"
        role="dialog"
      >
        <motion.div
          className="surface-panel max-w-lg w-full max-h-[90vh] overflow-y-auto"
          initial={{ scale: 0.8, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.8, opacity: 0, y: 20 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-6 border-b border-hairline">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-display font-semibold text-primary flex items-center">
                <SparklesIcon className="w-6 h-6 mr-2 text-pine" />
                Create Your Vision
              </h2>
              <button
                type="button"
                onClick={() => !generating && onClose?.()}
                className="btn-ghost p-2 disabled:opacity-50"
                disabled={generating}
                aria-label="Close"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Step 1: Define the Goal */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="p-6"
            >
              <h3 className="text-xl font-display font-semibold text-primary mb-6">
                {suggestionGoal 
                  ? `Let's bring your '${suggestionGoal}' Vision to life.`
                  : "What's your financial goal?"
                }
              </h3>

              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-4 p-3 border border-danger/40 bg-pine-tint rounded-lg text-danger text-sm"
                >
                  {error}
                </motion.div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-secondary text-sm font-medium mb-2">
                    Vision Title
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="e.g., Trip to Japan, New Home, Emergency Fund"
                    className="input-editorial"
                  />
                </div>

                <div>
                  <label className="block text-secondary text-sm font-medium mb-2">
                    Financial Target
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-primary">$</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      name="target_amount"
                      value={formData.target_amount}
                      onChange={handleInputChange}
                      placeholder="10000"
                      className="input-editorial pl-8"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-secondary text-sm font-medium mb-2">
                    Current Progress (Optional)
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-primary">$</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      name="current_amount"
                      value={formData.current_amount}
                      onChange={handleInputChange}
                      placeholder="0"
                      className="input-editorial pl-8"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-secondary text-sm font-medium mb-2">
                    Description (Optional)
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Describe what this goal means to you..."
                    rows="3"
                    className="input-editorial resize-none"
                  />
                </div>
              </div>

              <div className="mt-6 space-y-4">
                <div className="text-center mb-4">
                  <p className="text-sm text-secondary mb-4">
                    Choose how you'd like to create your vision:
                  </p>
                </div>
                
                <motion.button
                  onClick={handleCreateWithoutImage}
                  disabled={generating || !canSubmitStep1}
                  className="w-full btn-primary py-4 disabled:opacity-50 flex items-center justify-center space-x-2"
                  whileHover={{ scale: generating || !canSubmitStep1 ? 1 : 1.02 }}
                  whileTap={{ scale: generating || !canSubmitStep1 ? 1 : 0.98 }}
                >
                  <span>{generating ? "Creating..." : "Create Vision Now (No Image)"}</span>
                  {generating && (
                    <div className="w-4 h-4 border-2 border-hairline border-t-pine rounded-full animate-spin"></div>
                  )}
                </motion.button>
                
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-hairline"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-bone-raised text-secondary">or</span>
                  </div>
                </div>
                
                <motion.button
                  onClick={handleDefineGoal}
                  className="w-full py-3 btn-secondary flex items-center justify-center space-x-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span>Create with AI Image</span>
                  <ArrowRightIcon className="w-4 h-4" />
                </motion.button>
                
                <p className="text-xs text-faint text-center">
                  You can always add an image later if you change your mind
                </p>
              </div>
            </motion.div>
          )}

          {/* Step 2: AI Generation */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="p-6 text-center"
            >
              <motion.div
                className="w-24 h-24 mx-auto mb-6 surface-panel rounded-full flex items-center justify-center"
                animate={{ 
                  rotate: generating ? 360 : 0,
                  scale: [1, 1.1, 1]
                }}
                transition={{ 
                  rotate: { duration: 2, repeat: generating ? Infinity : 0, ease: "linear" },
                  scale: { duration: 2, repeat: Infinity }
                }}
              >
                {generating ? (
                  <div className="w-12 h-12 border-4 border-pine border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <PhotoIcon className="w-12 h-12 text-pine" />
                )}
              </motion.div>

              <h3 className="text-xl font-display font-semibold text-primary mb-4">
                {generating ? "Creating Your Vision..." : "Ready to Generate"}
              </h3>
              
              <p className="text-secondary mb-6 leading-relaxed">
                {generating 
                  ? (formData.generate_image 
                      ? "Your personal AI is creating an inspiration for you. This magic takes a moment... (up to ~2 minutes for high-quality images)"
                      : "Creating your vision without an image...")
                  : "Choose whether to generate an AI image for your vision or create it without one."
                }
              </p>

              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-4 p-3 border border-danger/40 bg-pine-tint rounded-lg text-danger text-sm"
                >
                  {error}
                </motion.div>
              )}

              {!generating && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6"
                >
                  <div className="mb-4 p-4 bg-bone border border-hairline rounded-lg">
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="generate_image"
                        checked={formData.generate_image}
                        onChange={handleInputChange}
                        className="checkbox-editorial"
                      />
                      <div>
                        <span className="text-sm font-medium text-primary">Generate AI Image</span>
                        <p className="text-xs text-secondary">
                          Create a beautiful, personalized image to represent your vision
                        </p>
                      </div>
                    </label>
                  </div>

                  {formData.generate_image && (
                    <details className="text-left">
                      <summary className="text-sm text-pine cursor-pointer hover:text-primary transition-colors duration-300 mb-3">
                        Customize Image (Optional)
                      </summary>
                      <textarea
                        name="image_prompt"
                        value={formData.image_prompt}
                        onChange={handleInputChange}
                        placeholder="Describe how you'd like your vision image to look..."
                        rows="2"
                        className="input-editorial resize-none text-sm"
                      />
                    </details>
                  )}
                </motion.div>
              )}

              <div className="space-y-3">
                <div className="flex space-x-4">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    disabled={generating}
                    className="flex-1 py-3 btn-secondary disabled:opacity-50"
                  >
                    Back
                  </button>
                  <motion.button
                    type="button"
                    onClick={handleCreateVision}
                    disabled={generating}
                    className="flex-1 py-3 btn-primary disabled:opacity-50"
                    whileHover={{ scale: generating ? 1 : 1.02 }}
                    whileTap={{ scale: generating ? 1 : 0.98 }}
                  >
                    {generating 
                      ? (formData.generate_image ? "Generating..." : "Creating...") 
                      : (formData.generate_image ? "Generate Vision" : "Create Vision")
                    }
                  </motion.button>
                </div>
                
                {!generating && (
                  <motion.button
                    type="button"
                    onClick={handleCreateWithoutImage}
                    className="w-full py-2 text-sm btn-ghost"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Skip image and create vision now
                  </motion.button>
                )}
              </div>
            </motion.div>
          )}

          {/* Step 3: Success */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="p-6 text-center"
            >
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="mb-6"
              >
                {generatedImage ? (
                  <img
                    src={generatedImage}
                    alt={formData.title}
                    className="w-full h-48 object-cover rounded-xl shadow-2xl"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                ) : (
                  <div className="w-full h-48 bg-pine-tint rounded-lg flex items-center justify-center border border-hairline">
                    <div className="text-center px-4">
                      <div className="w-16 h-16 bg-pine rounded-full flex items-center justify-center mx-auto mb-4 surface-dark">
                        {imagePending ? (
                          <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                        ) : (
                          <CheckCircleIcon className="w-8 h-8" />
                        )}
                      </div>
                      <p className="text-secondary">
                        {imagePending
                          ? 'Creating your cover image…'
                          : formData.generate_image
                            ? 'Vision created — image will appear on your board shortly.'
                            : 'Vision created!'}
                      </p>
                    </div>
                  </div>
                )}
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <div className="flex items-center justify-center mb-4">
                  <CheckCircleIcon className="w-8 h-8 text-pine mr-2" />
                  <h3 className="text-xl font-display font-semibold text-primary">
                    Vision Created!
                  </h3>
                </div>
                
                <p className="text-secondary mb-6">
                  Your <span className="text-pine font-semibold">{formData.title}</span> vision 
                  has been added to your dashboard. 
                  {formData.generate_image 
                    ? " Let it inspire you on your journey!"
                    : " You can always add an image later if you'd like."
                  }
                </p>
                
                <motion.button
                  type="button"
                  onClick={() => {
                    onVisionCreated?.();
                    onClose?.();
                  }}
                  className="w-full py-4 btn-primary"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Continue to Dashboard
                </motion.button>
              </motion.div>
            </motion.div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default VisionCreationModal;
